import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StrikerBrand } from '@striker/ui-shell';

type Step = 'register' | 'otp' | 'done';

export function EndUserRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Registration form
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  // OTP form
  const [otpCode, setOtpCode] = useState('');

  async function onRegister(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/auth/end-user/register', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim(),
          password,
          display_name: displayName.trim(),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
        pending_end_user_id?: number;
        dev_otp_code?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `Registrazione fallita (${res.status})`);
      // In dev mode, pre-fill the OTP code
      if (body.dev_otp_code) {
        setOtpCode(body.dev_otp_code);
      }
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/auth/end-user/verify-otp', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp_code: otpCode.trim() }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `Verifica fallita (${res.status})`);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sk-login">
      <div className="sk-login__card">
        <div className="sk-login__brand">
          <StrikerBrand surface="dark" title="StrikerPos" />
        </div>

        {step === 'register' && (
          <>
            <h1 className="sk-page__title">Crea il tuo Passaporto StrikerPos</h1>
            <p className="sk-page__lead">
              Un account unico per prenotare in tutti i locali del circuito.
              Accumula reputation, fiches e badge.
            </p>

            {error ? <p className="sk-error">{error}</p> : null}

            <form onSubmit={onRegister}>
              <label className="sk-field">
                <span>Email</span>
                <input
                  className="sk-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="sk-field">
                <span>Telefono (E.164, es. +393331234567)</span>
                <input
                  className="sk-input"
                  type="tel"
                  required
                  placeholder="+39 333 1234567"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label className="sk-field">
                <span>Password (min 8 caratteri)</span>
                <input
                  className="sk-input"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label className="sk-field">
                <span>Nome visualizzato</span>
                <input
                  className="sk-input"
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Come vuoi apparire nelle recensioni"
                  required
                />
              </label>

              <button type="submit" className="sk-btn sk-btn--block" disabled={busy || !displayName.trim()}>
                {busy ? 'Invio…' : 'Registrati'}
              </button>
            </form>

            <p className="sk-muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
              Ti invieremo un codice via SMS per verificare il telefono.
            </p>

            <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <Link to="/login" style={{ fontSize: '0.875rem' }}>
                ← Torna al login
              </Link>
            </p>
          </>
        )}

        {step === 'otp' && (
          <>
            <h1 className="sk-page__title">Verifica il tuo telefono</h1>
            <p className="sk-page__lead">
              Abbiamo inviato un codice a 6 cifre al numero {phone}.
              Inseriscilo qui sotto per completare la registrazione.
            </p>

            {error ? <p className="sk-error">{error}</p> : null}

            <form onSubmit={onVerifyOtp}>
              <label className="sk-field">
                <span>Codice OTP</span>
                <input
                  className="sk-input"
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>

              <button type="submit" className="sk-btn sk-btn--block" disabled={busy}>
                {busy ? 'Verifica…' : 'Verifica codice'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="sk-register__linkish"
                onClick={() => setStep('register')}
                style={{ fontSize: '0.875rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
              >
                ← Modifica dati
              </button>
            </p>
          </>
        )}

        {step === 'done' && (
          <>
            <h1 className="sk-page__title">Account verificato!</h1>
            <p className="sk-page__lead">
              Il tuo Passaporto StrikerPos è pronto. Ora puoi accedere
              e iniziare a prenotare nei locali del circuito.
            </p>

            <button
              type="button"
              className="sk-btn sk-btn--block"
              onClick={() => navigate('/login')}
            >
              Vai al login
            </button>
          </>
        )}
      </div>

      {step === 'register' && (
        <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem' }}>
          Sei un locale?{' '}
          <Link to="/register" style={{ fontWeight: 600 }}>
            Registrati come ristoratore →
          </Link>
        </p>
      )}
    </div>
  );
}
